/**
 * CAPTCHA Provider Component
 *
 * Global context provider for managing CAPTCHA state.
 * Wraps the application and handles both invisible and visible CAPTCHA challenges.
 * Uses custom script loader - NO npm package dependencies.
 *
 * @author AmalQ Frontend Team
 * @date 30-12-2025
 */

import React, {
  createContext,
  useState,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';
import type {
  CaptchaState,
  CaptchaError,
  CaptchaChallenge,
  CaptchaContextValue,
  CaptchaErrorCode,
} from './types';
import { executeInvisible } from './hcaptchaLoader';
import {
  CAPTCHA_ERROR_MESSAGES,
  isRecoverableError,
  MAX_CAPTCHA_RETRIES,
} from './constants';
import { CaptchaWidget } from './CaptchaWidget';
import { VerifyingIndicator } from './VerifyingIndicator';

/**
 * CAPTCHA Context
 * Provides CAPTCHA state and methods to child components
 */
export const CaptchaContext = createContext<CaptchaContextValue | null>(null);

/**
 * Props for CaptchaProvider
 */
interface CaptchaProviderProps {
  children: ReactNode;
}

/**
 * Promise resolver type for visible CAPTCHA
 */
interface ChallengeResolver {
  resolve: (token: string) => void;
  reject: (error: CaptchaError) => void;
}

/**
 * Create a CaptchaError object
 */
const createError = (
  code: CaptchaErrorCode,
  customMessage?: string
): CaptchaError => ({
  code,
  message: customMessage || CAPTCHA_ERROR_MESSAGES[code],
  recoverable: isRecoverableError(code),
});

/**
 * Delay utility for retry logic
 */
const delay = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Get effective siteKey with fallback to env variable
 * Backend should always provide siteKey, but fallback ensures robustness
 */
const getEffectiveSiteKey = (challengeSiteKey?: string): string => {
  const envSiteKey = import.meta.env.VITE_HCAPTCHA_SITE_KEY;
  const siteKey = challengeSiteKey || envSiteKey;

  if (!siteKey) {
    console.error('CAPTCHA: No siteKey available from backend or environment');
    throw new Error('CAPTCHA configuration error: No site key available');
  }

  return siteKey;
};

/**
 * CaptchaProvider Component
 *
 * Provides global CAPTCHA state management.
 * Automatically renders VerifyingIndicator and CaptchaWidget when needed.
 *
 * Usage:
 * ```tsx
 * <CaptchaProvider>
 *   <App />
 * </CaptchaProvider>
 * ```
 */
export const CaptchaProvider: React.FC<CaptchaProviderProps> = ({ children }) => {
  // State
  const [state, setState] = useState<CaptchaState>('idle');
  const [error, setError] = useState<CaptchaError | null>(null);
  const [currentChallenge, setCurrentChallenge] = useState<CaptchaChallenge | null>(null);

  // Ref to store the promise resolver for visible CAPTCHA
  const resolverRef = useRef<ChallengeResolver | null>(null);

  /**
   * Reset CAPTCHA state to idle
   */
  const reset = useCallback(() => {
    setState('idle');
    setError(null);
    setCurrentChallenge(null);
    resolverRef.current = null;
  }, []);

  /**
   * Execute invisible CAPTCHA with retry logic
   * Retries up to MAX_CAPTCHA_RETRIES times with delays
   */
  const executeInvisibleWithRetry = useCallback(
    async (siteKey: string): Promise<string> => {
      const retryDelays = [0, 2000, 3000]; // 0ms, 2s, 3s delays between attempts

      for (let attempt = 0; attempt < MAX_CAPTCHA_RETRIES; attempt++) {
        try {
          // Wait before retry (except first attempt)
          if (attempt > 0) {
            await delay(retryDelays[attempt] || 3000);
          }

          const token = await executeInvisible(siteKey);
          return token;
        } catch (err) {
          const captchaError =
            err && typeof err === 'object' && 'code' in err
              ? (err as CaptchaError)
              : createError('EXECUTION_FAILED');

          // If not recoverable or last attempt, throw
          if (!captchaError.recoverable || attempt === MAX_CAPTCHA_RETRIES - 1) {
            throw captchaError;
          }

          // Log and continue to next retry
          console.warn(
            `CAPTCHA attempt ${attempt + 1}/${MAX_CAPTCHA_RETRIES} failed, retrying...`,
            captchaError.message
          );
        }
      }

      // Should not reach here
      throw createError('EXECUTION_FAILED');
    },
    []
  );

  /**
   * Show a CAPTCHA challenge
   *
   * For invisible mode: Executes challenge in background with retry
   * For visible mode: Shows modal widget and waits for user
   *
   * @param challenge - Challenge data from backend
   * @returns Promise that resolves with hCaptcha token
   */
  const showChallenge = useCallback(
    async (challenge: CaptchaChallenge): Promise<string> => {
      // Reset any previous state
      setError(null);

      // Get effective siteKey with fallback to env variable
      const effectiveSiteKey = getEffectiveSiteKey(challenge.siteKey);

      // Store challenge with resolved siteKey
      const resolvedChallenge: CaptchaChallenge = {
        ...challenge,
        siteKey: effectiveSiteKey,
      };
      setCurrentChallenge(resolvedChallenge);

      if (challenge.captchaMode === 'invisible') {
        // Invisible mode - execute in background with retry
        setState('verifying');

        try {
          const token = await executeInvisibleWithRetry(effectiveSiteKey);
          setState('completed');
          // Small delay before resetting to idle for UI feedback
          setTimeout(reset, 500);
          return token;
        } catch (captchaError) {
          const err = captchaError as CaptchaError;

          // If invisible fails after retries, fall back to visible mode
          console.warn('Invisible CAPTCHA failed after retries, showing visible challenge');
          setError(err);
          setState('challenged');

          // Return a promise that waits for visible challenge completion
          return new Promise<string>((resolve, reject) => {
            resolverRef.current = { resolve, reject };
          });
        }
      } else {
        // Visible mode - show widget and wait for user
        setState('challenged');

        return new Promise<string>((resolve, reject) => {
          resolverRef.current = { resolve, reject };
        });
      }
    },
    [executeInvisibleWithRetry, reset]
  );

  /**
   * Handle successful verification from visible widget
   */
  const handleWidgetVerify = useCallback(
    (token: string) => {
      setState('completed');
      if (resolverRef.current) {
        resolverRef.current.resolve(token);
        resolverRef.current = null;
      }
      // Small delay before resetting for UI feedback
      setTimeout(reset, 500);
    },
    [reset]
  );

  /**
   * Handle error from visible widget
   */
  const handleWidgetError = useCallback((widgetError: CaptchaError) => {
    setError(widgetError);
    // Don't reject immediately for visible mode - let user retry
  }, []);

  /**
   * Handle token expiration from visible widget
   */
  const handleWidgetExpire = useCallback(() => {
    setError(createError('TOKEN_EXPIRED'));
    // Don't reject - user can retry
  }, []);

  /**
   * Handle user closing the visible widget
   */
  const handleWidgetClose = useCallback(() => {
    const closeError = createError('CHALLENGE_CLOSED');
    setError(closeError);
    if (resolverRef.current) {
      resolverRef.current.reject(closeError);
      resolverRef.current = null;
    }
    reset();
  }, [reset]);

  // Context value
  const contextValue: CaptchaContextValue = {
    state,
    error,
    showChallenge,
    reset,
  };

  return (
    <CaptchaContext.Provider value={contextValue}>
      {children}

      {/* Subtle "Verifying..." indicator for invisible CAPTCHA */}
      <VerifyingIndicator visible={state === 'verifying'} />

      {/* Visible CAPTCHA widget modal */}
      {state === 'challenged' && currentChallenge && (
        <CaptchaWidget
          siteKey={currentChallenge.siteKey}
          onVerify={handleWidgetVerify}
          onError={handleWidgetError}
          onExpire={handleWidgetExpire}
          onClose={handleWidgetClose}
          error={error}
        />
      )}
    </CaptchaContext.Provider>
  );
};

export default CaptchaProvider;
